'use client'

import { isbot } from 'isbot'

import useCookiesConsent from '@hooks/useCookiesConsent'
import { getCookieConsentValue } from '@lib/helpers/client'
import { type ReactNode, type JSX, createContext, useEffect, useState, useCallback } from 'react'

const REDDIT_SCRIPT_ID = 'rd_px'

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    rdt?: {
      (action: 'init', pixelId: string): void
      (action: 'track', event: string, data?: Record<string, unknown>): void
      callQueue?: unknown[]
      sendEvent?: (...args: unknown[]) => void
    }
  }
}

function isScriptAppended(): boolean {
  return document.getElementById(REDDIT_SCRIPT_ID) !== null
}

function appendScript(): void {
  const pixelScript = `!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','t2_4d2hfmtyw');rdt('track', 'PageVisit');`

  const script = document.createElement('script')
  script.innerHTML = pixelScript
  script.id = REDDIT_SCRIPT_ID
  document.head.appendChild(script)
}

type RedditPixelContextType = {
  trackEvent: (event: string, data?: Record<string, unknown>) => void
  hasInitialized: boolean
  isLoading: boolean
  hasError: boolean
}

const RedditPixelContext = createContext<RedditPixelContextType | undefined>(undefined)

export function RedditPixelProvider({ children, isProd }: { children: ReactNode; isProd: boolean }): JSX.Element {
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [areCookiesAccepted] = useCookiesConsent()

  const initializeRedditPixel = useCallback(async (): Promise<void> => {
    if (!isProd) {
      console.warn('Reddit Pixel initialization skipped in dev environment')
      setIsLoading(false)
      setHasInitialized(false)
      setHasError(false)
      return
    }
    // appendScript ends in rdt('track','PageVisit'), so injecting the script is itself a
    // conversion event — the crawler guard in sendEventToAnalytics never gets a say. Crawlers
    // that run JS and carry a consented profile reach here; our own smoke suite is one, it clicks
    // Accept in its beforeEach. Bot hits on a conversion pixel degrade ad targeting.
    if (isbot(navigator.userAgent)) {
      setIsLoading(false)
      return
    }
    // The mount effect and the 500ms `cookies-update` listener below can both reach this point once
    // `areCookiesAccepted` flips (S20-842). appendScript() ends in rdt('track','PageVisit'), so a
    // second call is a duplicate conversion event, not just a duplicate script tag.
    if (isScriptAppended()) {
      return
    }
    try {
      appendScript()
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Reddit Pixel initialization timeout'))
        }, 5000)
        const checkRdt = (): void => {
          if (window.rdt) {
            clearTimeout(timeout)
            resolve()
          } else {
            setTimeout(checkRdt, 100)
          }
        }
        checkRdt()
      })
      setHasInitialized(true)
      setHasError(false)
    } catch (error) {
      console.error('Reddit Pixel initialization failed:', error)
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }, [isProd])

  useEffect(() => {
    const callback = (): void => {
      setTimeout(async () => {
        const cookiesAccepted = getCookieConsentValue()
        if (cookiesAccepted) {
          await initializeRedditPixel()
        }
      }, 500)
    }
    window.addEventListener('cookies-update', callback)
    return (): void => {
      window.removeEventListener('cookies-update', callback)
    }
  }, [initializeRedditPixel])

  useEffect(() => {
    if (!hasInitialized && !isScriptAppended() && areCookiesAccepted) {
      setIsLoading(true)
      initializeRedditPixel()
    }
  }, [areCookiesAccepted, initializeRedditPixel, hasInitialized])

  const trackEvent = (event: string, data?: Record<string, unknown>): void => {
    if (isScriptAppended() && window.rdt) {
      window.rdt('track', event, data)
    } else {
      console.warn('Reddit Pixel not initialized')
    }
  }

  return (
    <RedditPixelContext.Provider
      value={{
        trackEvent,
        hasInitialized: hasInitialized,
        isLoading: isLoading,
        hasError: hasError,
      }}
    >
      {children}
    </RedditPixelContext.Provider>
  )
}
